/**
 * TypingEngine — pure, framework-agnostic typing state.
 *
 * The caller drives time by calling `tick(nowMs)` with a monotonic timestamp
 * (e.g., `performance.now()`). Elapsed time is computed from samples, not from
 * an interval counter, so tab suspension cannot inflate elapsed time.
 *
 * @module typing-engine/engine
 */

import {
  accuracy as calcAccuracy,
  grossWpm as calcGross,
  netWpm as calcNet,
} from './formulas';

/**
 * @typedef {Object} EngineState
 * @property {string[]} expectedChars - Grapheme-aware expected characters.
 * @property {number} position - Index into expectedChars of the next expected char.
 * @property {number} totalTyped - Committed keystrokes (correct + incorrect).
 * @property {number} correctTyped - Correct committed keystrokes.
 * @property {number} incorrectTyped - Incorrect committed keystrokes.
 * @property {number} backspaces - Total backspaces pressed.
 * @property {Map<string, { correct: number, incorrect: number }>} keyStats
 * @property {number} startedAtMs - Timestamp of first keystroke.
 * @property {number} lastSampleMs - Last tick timestamp.
 * @property {number} accumulatedMs - Active elapsed ms (sum of active spans).
 * @property {boolean} paused - Whether the clock is paused.
 * @property {boolean} finished - Whether the run has ended.
 */

/**
 * Creates a new typing engine instance.
 * @param {string} expectedText - The text the user must reproduce.
 * @returns {TypingEngine}
 */
export function createEngine(expectedText) {
  if (typeof expectedText !== 'string') {
    throw new TypeError('createEngine: expectedText must be a string');
  }

  /** @type {EngineState} */
  const state = {
    expectedChars: Array.from(expectedText),
    position: 0,
    totalTyped: 0,
    correctTyped: 0,
    incorrectTyped: 0,
    backspaces: 0,
    keyStats: new Map(),
    startedAtMs: 0,
    lastSampleMs: 0,
    accumulatedMs: 0,
    paused: false,
    finished: false,
  };

  return new TypingEngine(state);
}

class TypingEngine {
  /** @param {EngineState} state */
  constructor(state) {
    this._state = state;
    this._started = false;
  }

  /** @returns {number} */
  get position() {
    return this._state.position;
  }

  /** @returns {string[]} */
  get expectedChars() {
    return this._state.expectedChars.slice();
  }

  /** @returns {number} */
  get totalTyped() {
    return this._state.totalTyped;
  }

  /** @returns {number} */
  get correctTyped() {
    return this._state.correctTyped;
  }

  /** @returns {number} */
  get incorrectTyped() {
    return this._state.incorrectTyped;
  }

  /** @returns {number} */
  get backspaces() {
    return this._state.backspaces;
  }

  /** @returns {boolean} */
  get finished() {
    return this._state.finished;
  }

  /** @returns {boolean} */
  get paused() {
    return this._state.paused;
  }

  /** @returns {boolean} */
  get started() {
    return this._started;
  }

  /** @returns {number} Active elapsed milliseconds (excluding paused spans). */
  get elapsedMs() {
    return this._state.accumulatedMs;
  }

  /**
   * Feeds a batch of keystrokes. Characters that don't map to the expected
   * position are counted as incorrect and advance the position. When position
   * reaches expectedChars.length, the run is marked finished.
   * @param {string} input
   * @param {number} nowMs - Monotonic timestamp (e.g., performance.now()).
   */
  type(input, nowMs) {
    if (this._state.finished) return;
    if (typeof input !== 'string' || input.length === 0) return;

    if (!this._started) {
      this._started = true;
      this._state.startedAtMs = nowMs;
      this._state.lastSampleMs = nowMs;
    } else if (!this._state.paused) {
      // Attribute time since last sample to the active span.
      this._state.accumulatedMs += Math.max(0, nowMs - this._state.lastSampleMs);
      this._state.lastSampleMs = nowMs;
    }

    const chars = Array.from(input);
    for (const ch of chars) {
      if (this._state.position >= this._state.expectedChars.length) break;
      const expected = this._state.expectedChars[this._state.position];
      const isCorrect = ch === expected;

      this._state.totalTyped += 1;
      if (isCorrect) {
        this._state.correctTyped += 1;
      } else {
        this._state.incorrectTyped += 1;
      }

      const key = expected.toLowerCase();
      const stats = this._state.keyStats.get(key) || { correct: 0, incorrect: 0 };
      if (isCorrect) stats.correct += 1;
      else stats.incorrect += 1;
      this._state.keyStats.set(key, stats);

      this._state.position += 1;
    }

    if (this._state.position >= this._state.expectedChars.length) {
      this._state.finished = true;
    }
  }

  /**
   * Removes the last committed character. Position cannot go below 0.
   * Backspace reverses the position but does NOT undo the error count,
   * because the original mistake was already committed. The user's accuracy
   * reflects their keystrokes, not their final text.
   */
  backspace() {
    if (this._state.finished) return;
    if (this._state.position === 0) return;
    this._state.position -= 1;
    this._state.backspaces += 1;
    // Note: totalTyped/correctTyped/incorrectTyped are NOT decremented.
    // Accuracy is over all keystrokes. This matches Monkeytype and 10fastfingers.
  }

  /**
   * Marks the active span as ended (e.g., on tab blur or timeout). Elapsed
   * milliseconds are accumulated; subsequent ticks restart the active span.
   * @param {number} nowMs
   */
  pause(nowMs) {
    if (this._state.paused || this._state.finished || !this._started) return;
    this._state.accumulatedMs += Math.max(0, nowMs - this._state.lastSampleMs);
    this._state.lastSampleMs = nowMs;
    this._state.paused = true;
  }

  /**
   * Resumes tracking. Elapsed time excludes the paused span.
   * @param {number} nowMs
   */
  resume(nowMs) {
    if (!this._state.paused || this._state.finished) return;
    this._state.paused = false;
    this._state.lastSampleMs = nowMs;
  }

  /**
   * No-op if the run is already finished or not started. Idempotent so callers
   * can safely call `finish()` on double-submit paths.
   * @param {number} nowMs
   */
  finish(nowMs) {
    if (this._state.finished) return;
    if (this._started && !this._state.paused) {
      this._state.accumulatedMs += Math.max(0, nowMs - this._state.lastSampleMs);
      this._state.lastSampleMs = nowMs;
    }
    this._state.finished = true;
  }

  /**
   * Live metrics snapshot. Safe to call at any time (before or after finish).
   * @returns {{
   *   grossWpm: number,
   *   netWpm: number,
   *   accuracy: number,
   *   correctChars: number,
   *   incorrectChars: number,
   *   totalChars: number,
   *   backspaces: number,
   *   elapsedSeconds: number,
   *   progress: number,
   *   finished: boolean
   * }}
   */
  getMetrics() {
    const elapsedSeconds = this._state.accumulatedMs / 1000;
    const totalChars = this._state.totalTyped;
    const correctChars = this._state.correctTyped;
    const incorrectChars = this._state.incorrectTyped;
    const totalExpected = this._state.expectedChars.length;
    const progress =
      totalExpected === 0 ? 0 : Math.min(1, this._state.position / totalExpected);

    return {
      grossWpm: calcGross(totalChars, elapsedSeconds),
      netWpm: calcNet(correctChars, elapsedSeconds),
      accuracy: calcAccuracy(correctChars, totalChars),
      correctChars,
      incorrectChars,
      totalChars,
      backspaces: this._state.backspaces,
      elapsedSeconds,
      progress,
      finished: this._state.finished,
    };
  }

  /**
   * Returns weak keys sorted by error rate (highest first).
   * Only keys with at least `minAttempts` attempts are included.
   * @param {number} [minAttempts=3]
   * @param {number} [limit=10]
   * @returns {Array<{ key: string, correct: number, incorrect: number, errorRate: number }>}
   */
  getWeakKeys(minAttempts = 3, limit = 10) {
    const list = [];
    for (const [key, stats] of this._state.keyStats) {
      const attempts = stats.correct + stats.incorrect;
      if (attempts < minAttempts) continue;
      list.push({
        key,
        correct: stats.correct,
        incorrect: stats.incorrect,
        errorRate: stats.incorrect / attempts,
      });
    }
    list.sort((a, b) => b.errorRate - a.errorRate || b.incorrect - a.incorrect);
    return list.slice(0, limit);
  }

  /**
   * Immutable attempt record. The caller supplies activity metadata.
   * @param {{
   *   activityType: 'typing_test' | 'lesson' | 'practice' | 'story' | 'game',
   *   activityRefSlug?: string,
   * }} meta
   * @returns {{
   *   activityType: string,
   *   activityRefSlug: string | null,
   *   durationSeconds: number,
   *   grossWpm: number,
   *   netWpm: number,
   *   accuracy: number,
   *   correctChars: number,
   *   incorrectChars: number,
   *   totalChars: number,
   *   backspaces: number,
   * }}
   */
  toAttemptRecord(meta) {
    const m = this.getMetrics();
    return {
      activityType: meta.activityType,
      activityRefSlug: meta.activityRefSlug || null,
      durationSeconds: Math.max(1, Math.round(m.elapsedSeconds)),
      grossWpm: m.grossWpm,
      netWpm: m.netWpm,
      accuracy: m.accuracy,
      correctChars: m.correctChars,
      incorrectChars: m.incorrectChars,
      totalChars: m.totalChars,
      backspaces: m.backspaces,
    };
  }
}

/**
 * Convenience helper: run a completed, already-validated attempt through
 * all formulas and produce a consistent result object.
 * @param {ReturnType<TypingEngine['getMetrics']>} metrics
 * @returns {{ grossWpm: number, netWpm: number, accuracy: number }}
 */
export function summarize(metrics) {
  return {
    grossWpm: metrics.grossWpm,
    netWpm: metrics.netWpm,
    accuracy: metrics.accuracy,
  };
}